import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { authSelector } from '../features/auth/slice';
import SectionTabs from '../components/SectionTabs';
import { Users, Disc, Music, Globe, User, Library, ListMusic, Package, Boxes } from 'lucide-react';
import usePhysicalExists from '../hooks/usePhysicalExists';

const TYPE_BADGE = {
  CD: 'bg-sky-500/10 text-sky-400',
  LP: 'bg-violet-500/10 text-violet-400',
  굿즈: 'bg-amber-500/10 text-amber-400',
};

function ByPhysicalTotalPage() {
  const { me } = useSelector(authSelector.selectAuthState);
  const isAdmin = me?.adminYn === 'Y';
  const hasPhysical = usePhysicalExists();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!me) return;
    setLoading(true);
    axios.get('/api/physical-sales/total')
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [me]);

  const reportTabs = [
    { label: isAdmin ? '당월권리사별' : '월별리포트', to: '/ByClientLatestMonth', icon: Users },
    { label: '당월앨범별', to: '/ByMonthAlbums', icon: Disc },
    { label: '당월곡별', to: '/ByMonthSongs', icon: Music },
    { label: '당월플랫폼별', to: '/ByMonthPlatforms', icon: Globe },
    { label: '당월아티스트별', to: '/ByMonthArtists', icon: User },
    ...(hasPhysical ? [{ label: '당월음반별', to: '/ByPhysicalMonth', icon: Package }] : []),
    { label: '앨범별누적', to: '/ByAlbums', icon: Library },
    { label: '곡별누적', to: '/BySongs', icon: ListMusic },
    ...(hasPhysical ? [{ label: '음반누적', to: '/ByPhysicalTotal', icon: Boxes }] : []),
  ];

  const colSpanFull = isAdmin ? 10 : 9;
  const colSpanSum = isAdmin ? 5 : 4;

  return (
    <>
      <div className="flex mb-8 justify-center items-center">
        <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">음반 누적 정산</h1>
      </div>
      <SectionTabs items={reportTabs} />

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-sky-500 mr-3" />
          <div className="text-sm text-slate-500">데이터 불러오는 중...</div>
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-2xl border border-white/[0.08] bg-[#182334]">
          <table className="min-w-full text-sm text-left">
            <thead>
              <tr className="text-xs text-slate-400 uppercase border-b border-white/[0.06]">
                <th className="px-4 py-3">순번</th>
                {isAdmin && <th className="px-4 py-3">권리사</th>}
                <th className="px-4 py-3">앨범명</th>
                <th className="px-4 py-3">분류</th>
                <th className="px-4 py-3">품번</th>
                <th className="px-4 py-3 text-right">총수량</th>
                <th className="px-4 py-3 text-right">총판매금액</th>
                <th className="px-4 py-3 text-right">총수수료</th>
                <th className="px-4 py-3 text-right">총정산액</th>
                <th className="px-4 py-3 text-right">현재재고</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td colSpan={colSpanFull} className="px-4 py-12 text-center text-slate-500">데이터 없음</td>
                </tr>
              ) : data.map((row, i) => (
                <tr key={`${row.clientId}-${row.physicalAlbumId}`} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                  <td className="px-4 py-3 text-slate-400">{i + 1}</td>
                  {isAdmin && <td className="px-4 py-3 font-medium text-white">{row.client?.name}</td>}
                  <td className="px-4 py-3 font-medium text-white">{row.physicalAlbum?.album?.nameKr}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${TYPE_BADGE[row.physicalAlbum?.type] || 'bg-slate-500/10 text-slate-400'}`}>
                      {row.physicalAlbum?.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-400 font-mono text-xs">{row.physicalAlbum?.productCode}</td>
                  <td className="px-4 py-3 text-right">{Number(row.quantity).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">{Number(row.saleAmount).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-rose-400">{Number(row.feeAmount).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-bold text-emerald-400">{Number(row.clientAmount).toLocaleString()}</td>
                  <td className={`px-4 py-3 text-right font-bold ${(row.physicalAlbum?.stock ?? 0) <= 0 ? 'text-rose-400' : 'text-slate-300'}`}>
                    {Number(row.physicalAlbum?.stock ?? 0).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
            {data.length > 0 && (
              <tfoot>
                <tr className="bg-white/[0.03] border-t border-white/[0.08]">
                  <td colSpan={colSpanSum} className="px-4 py-3 text-xs text-slate-400 font-semibold">합계</td>
                  <td className="px-4 py-3 text-right font-bold text-white">{data.reduce((s, r) => s + r.quantity, 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-bold text-white">{data.reduce((s, r) => s + Number(r.saleAmount), 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-bold text-rose-400">{data.reduce((s, r) => s + Number(r.feeAmount), 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-bold text-emerald-400">{data.reduce((s, r) => s + Number(r.clientAmount), 0).toLocaleString()}</td>
                  <td className="px-4 py-3"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </>
  );
}

export default ByPhysicalTotalPage;
